import { countUpAction } from "@/js/helpers";

/**
 * 都道府県TOP（エリアTOP）のエリア検索フォーム制御 + 該当数表示。
 *
 * 構造はエリア検索ページ（search.area）と同じ 地域グループ(area[]) → 市区町村(sub_area[]) の2階層。
 *
 * - グループチェック ↔ 配下の市区 の連動（部分選択は indeterminate）。
 *   グループを丸ごと選んだときは送信直前に配下の市区を送信対象から外し、area[] だけを送る
 * - 市区の開閉アコーディオン（SPのみ表示）
 * - 該当数（[data-home-search-count] / [data-home-search-count-lot]）は選択中の市区の合計。
 *   未選択時は0で、送信ボタンも無効（search.area と同じ「チェックしたら検索できる」挙動）。
 *   分譲地＝多棟現場の親の数、プラン＝それ以外の物件数の2本立て
 * - 種別チェックボックス変更時は search.area.counts で件数を取り直す（miraieへは行かずマスタキャッシュ）
 * - フローティングCTA: SPは「エリアから探す」ブロック内だけ検索バー、PCは出さない（フォーム内の送信ボタンで足りる）
 *
 * ※フォーム本体（pages.home._search-area）は LoadContents プラグインでAjax読み込みされるため、
 *   ページ初期化時点ではDOMに存在しない。プレースホルダ（.js-load-contents）の
 *   onLoadContents イベントを受けて初期化する。
 *   innerHTML 代入ではインライン <script> が実行されないので、
 *   アコーディオンを含め注入後に必要な処理はすべてここに置くこと。
 *
 * ※送信はフォーム内の「この内容で検索する」と、下部フローティングの
 *   「検索する」の両方。後者はSP限定で、チェック時にCTAが検索へ切り替わるため残す
 */

const mqSp = window.matchMedia("(max-width: 768px)");
let inSearchBlock = false;
let latestHasSelection = false;
let floatingBound = false;

const setBarVisible = (visible) => {
    const bar = document.querySelector(".c-others-search-submit--home");
    if (!bar) return;
    bar.dataset.visible = visible ? "true" : "false";
};

const setFloatingSpHidden = (hidden) => {
    const floatingSp = document.querySelector(".p-home-floating-sp");
    if (!floatingSp) return;
    floatingSp.dataset.hidden = hidden ? "true" : "false";
    floatingSp.setAttribute("aria-hidden", hidden ? "true" : "false");
};

const syncFloating = (hasSelection = latestHasSelection) => {
    latestHasSelection = hasSelection;
    if (!document.querySelector(".c-others-search-submit--home")) return;

    if (!mqSp.matches) {
        // PC: フォーム内の「この内容で検索する」で完結するのでバーは出さない（CSSでも display:none）
        setBarVisible(false);
        setFloatingSpHidden(false);
        return;
    }

    // SP: 「エリアから探す」ブロック内だけ検索バー、それ以外は下部CTA
    if (inSearchBlock) {
        setBarVisible(true);
        setFloatingSpHidden(true);
    } else {
        setBarVisible(false);
        setFloatingSpHidden(false);
    }
};

const setupFloating = () => {
    if (floatingBound) return;
    floatingBound = true;

    const bar = document.querySelector(".c-others-search-submit--home");
    const searchBlock = document.querySelector(".p-home-search__area");
    if (bar && !bar.hasAttribute("data-hide-until-selected")) {
        bar.setAttribute("data-hide-until-selected", "");
    }
    if (bar) bar.dataset.visible = "false";

    if (searchBlock && typeof IntersectionObserver !== "undefined") {
        const observer = new IntersectionObserver(
            (entries) => {
                inSearchBlock = Boolean(entries[0]?.isIntersecting);
                syncFloating();
            },
            {
                root: null,
                rootMargin: "-25% 0px -35% 0px",
                threshold: 0,
            },
        );
        observer.observe(searchBlock);
    }

    const onViewportChange = () => syncFloating();
    if (typeof mqSp.addEventListener === "function") {
        mqSp.addEventListener("change", onViewportChange);
    } else if (typeof mqSp.addListener === "function") {
        mqSp.addListener(onViewportChange);
    }
};

/**
 * フォームへイベントを結び付ける。フォームが無いページ（総合TOP、
 * エリアマスタ不通でフォームが出せない都道府県）では何もしない。
 * 同じフォームに対して二重に実行されないよう data 属性で印を付ける。
 */
const setupSearchForm = () => {
    const form = document.querySelector("[data-home-search-form]");
    if (!form) return;
    if (form.dataset.homeSearchBound) return; // Ajax再読み込み以外での重複初期化を防ぐ
    form.dataset.homeSearchBound = "1";

    const countTargets = [...document.querySelectorAll("[data-home-search-count]")];
    // フォーム内と下部フローティングの2か所に同じ値を書く（どちらも同じ属性で拾う）
    const lotCountTargets = [...document.querySelectorAll("[data-home-search-count-lot]")];
    const barResult = document.querySelector(".c-others-search-submit--home .p-submit-result");
    const buttons = [
        ...document.querySelectorAll(".p-home-search__submit-btn"),
        ...document.querySelectorAll(".c-others-search-submit--home .c-buttons-submit"),
    ];
    // 地域グループ（area[]）と配下の市区町村（sub_area[]）。DOMの階層がAPIのレスポンス構造と1:1で対応する
    const groups = [...form.querySelectorAll(".p-home-search__group")]
        .map((el) => ({
            el,
            key: el.dataset.key || "",
            input: el.querySelector('.p-home-search__group-check input[type="checkbox"]'),
            countEl: el.querySelector(".p-home-search__group-count"),
            cities: [...el.querySelectorAll('.p-home-search__city input[type="checkbox"]')],
        }))
        .filter((group) => group.input && group.cities.length > 0);
    const cities = groups.flatMap((group) => group.cities);
    // 種別は2026-08-21にチェックボックス→ラジオ（単一選択）へ変更（設計メモ D25）
    const typeTabs = form.querySelector('.c-search-type-tabs[data-mode="radio"]');
    const typeInputs = typeTabs ? [...typeTabs.querySelectorAll('input[type="radio"]')] : [];
    const areaCountsUrl = form.dataset.areaCountsUrl || "";

    // 市区を1つも選んでいなければ検索できないため、初期値は0
    let displayCount = 0;
    let displayLotCount = 0;
    let hasSelection = false;

    // 種別が1つも選ばれていないと検索条件が成立しないため送信させない。
    // ラジオでは必ず1つ checked なので実際には常に true になるが、サーバが
    // checked を出し損ねた場合に送信を止める安全弁として残す
    const hasTypeSelected = () => typeInputs.length === 0 || typeInputs.some((input) => input.checked);

    const syncSelectionUi = () => {
        countTargets.forEach((target) => countUpAction(target, displayCount));
        // 分譲地は0でも隠さずそのまま出す（多棟コードを持たない種別は必ず0になる）
        lotCountTargets.forEach((target) => countUpAction(target, displayLotCount));

        // 市区を1つも選んでいないと送信させない（search.area と同じ「チェックしたら検索できる」挙動）
        const canSubmit = hasTypeSelected() && hasSelection;
        buttons.forEach((button) => {
            if (canSubmit) {
                button.removeAttribute("disabled");
            } else {
                button.setAttribute("disabled", "");
            }
        });

        if (barResult) {
            if (!hasSelection) barResult.dataset.empty = "";
            else delete barResult.dataset.empty;
        }
        syncFloating(hasSelection);
    };

    const updateSelection = () => {
        const checked = cities.filter((input) => input.checked);
        hasSelection = checked.length > 0;
        // 市区未選択は検索できないので0を出す。以前は未選択のまま送ると都道府県全体が
        // 検索される仕様だったため県の件数を出していたが、その前提が無くなった
        const sumChecked = (attr) =>
            checked.reduce((sum, input) => {
                const cell = input.closest("label")?.querySelector("[data-count]");
                return sum + Number(cell?.dataset[attr] || 0);
            }, 0);
        displayCount = hasSelection ? sumChecked("count") : 0;
        displayLotCount = hasSelection ? sumChecked("countLot") : 0;
        syncSelectionUi();
    };

    // 市区の選択状況からグループのチェック状態を決める（全選択=checked、一部=indeterminate）
    const syncGroupsFromCities = () => {
        groups.forEach((group) => {
            const checkedCount = group.cities.filter((input) => input.checked).length;
            group.input.checked = checkedCount === group.cities.length;
            group.input.indeterminate = checkedCount > 0 && checkedCount < group.cities.length;
        });
    };

    // グループ → 配下の市区を全選択/全解除
    const syncCitiesFromGroup = (group) => {
        group.input.indeterminate = false;
        group.cities.forEach((input) => {
            input.checked = group.input.checked;
        });
        syncGroupsFromCities();
        updateSelection();
    };

    // グループを丸ごと選んだときは、送信直前に配下の市区（sub_area[]）を送信対象から外す。
    //
    // 【なぜ外すか】area[]（グループ）は EstateCondTrait が配下の市区町村へ展開して
    // sub_area[] と union するので、両方送っても検索結果は同じ。外さないと
    // 「?area[]=p10_0&sub_area[]=…×35」という無駄に長いURLになる（群馬で実測）。
    // 検索ページ側の共通JS（components/others/_search-list.js）は親チェック時に子を
    // disabled にして area[] だけを送っており、そちらと形を揃える意味もある。
    //
    // 【UIには触らない】あちらは常時 disabled にするためチェックを外すのに2クリック要るが、
    // ここは送信の瞬間だけ disabled にするので、操作感は今までどおり。
    form.addEventListener('submit', () => {
        const disabled = [];
        groups.forEach((group) => {
            // グループのチェックが送信されることが前提。付いていないのに子を外すと
            // エリア条件そのものが消えるため、両方が揃っているときだけ外す
            if (group.input.disabled || !group.input.checked) return;
            if (!group.cities.every((input) => input.checked)) return;

            group.cities.forEach((input) => {
                input.disabled = true;
                disabled.push(input);
            });
        });

        // ブラウザバック（bfcache）で戻ったときに disabled が残らないよう元へ戻す。
        // フォームのシリアライズは submit イベントの直後に同期で走るので、
        // タイマーで戻しても送信内容には影響しない
        if (disabled.length > 0) {
            window.setTimeout(() => {
                disabled.forEach((input) => {
                    input.disabled = false;
                });
            }, 0);
        }
    });

    /* ---- 種別変更に応じた件数の再取得（search.area の area-type-filter.js と同じ流れ） ---- */

    let fetchToken = 0;
    let fetchTimerId = 0;

    const setTypeLoading = (loading) => {
        // 未キャッシュの種別は実APIを叩くため待ちが発生し得る。
        // 二重送信・結果の取り違えを防ぐため、取得中はラジオを一時的に無効化する
        typeTabs?.toggleAttribute("data-loading", loading);
        typeInputs.forEach((input) => {
            input.disabled = loading;
        });
    };

    // 表示はプランのまま。分譲地は画面に出さず data 属性でだけ持つ
    const setCount = (el, count, lotCount) => {
        if (!el) return;
        el.dataset.count = count;
        el.dataset.countLot = lotCount;
        el.textContent = count;
    };

    // レスポンス { cities: { "<グループキー>": { count, children: { "<行政区コード>": count } } } } は
    // 地域グループ → 市区町村 のDOM構造とそのまま対応する（search.area の area-type-filter.js と同じ）
    const applyCityCounts = (cityMap) => {
        groups.forEach((group) => {
            const data = cityMap?.[group.key];
            if (!data) return; // レスポンスに無いグループは前回値を残す

            group.el.dataset.count = data.count;
            setCount(group.countEl, data.count, data.lot_count ?? 0);

            group.cities.forEach((input) => {
                const count = data.children?.[input.value];
                if (count === undefined) return; // 同上（0で塗り潰さない）
                setCount(
                    input.closest("label")?.querySelector("[data-count]"),
                    count,
                    data.children_lot?.[input.value] ?? 0,
                );
            });
        });
    };

    const fetchCounts = async () => {
        if (!areaCountsUrl || cities.length === 0) return;

        const selected = typeInputs.find((input) => input.checked)?.value;
        // サーバが1つも checked を出さなかった場合の保険。直前の件数を残したまま送信だけ止める
        if (!selected) return;

        // 選ばれているのは必ず実在の4種別のどれか。
        // 【2026-08-21 「すべて」タブ（value=all）の撤去に伴い分岐を1つ削除】
        // 以前は「'all' のときは type を積まずに投げる」（サーバ側 EstateController::areaCounts が
        // type 未指定を 'all' へ丸めるのに対し、type[]=all と明示すると
        // normalizeEstateType() が 'all' を返して別経路に入るため）としていたが、
        // 'all' は選択肢から消えたので到達不能になった（search/area-type-filter.js と同じ判断）
        const params = new URLSearchParams();
        params.append("type[]", selected);

        const token = ++fetchToken;
        setTypeLoading(true);
        try {
            const res = await fetch(`${areaCountsUrl}?${params.toString()}`);
            if (!res.ok) throw new Error(`APIエラー: ${res.status}`);
            const { cities: cityMap } = await res.json();
            if (token !== fetchToken) return; // 後発のリクエストが走っているため破棄する
            applyCityCounts(cityMap);
            updateSelection();
        } catch (error) {
            // 失敗時は0で塗り潰さず、直前の件数をそのまま残す
            console.warn("エリア件数APIの取得に失敗しました:", error);
        } finally {
            if (token === fetchToken) setTypeLoading(false);
        }
    };

    typeInputs.forEach((input) => {
        input.addEventListener("change", () => {
            // 送信ボタンの活性だけは待たせず即時反映し、取得自体は連打をまとめて1回にする
            syncSelectionUi();
            window.clearTimeout(fetchTimerId);
            fetchTimerId = window.setTimeout(fetchCounts, 250);
        });
    });

    groups.forEach((group) => {
        // 地域グループ → 配下の市区町村を全選択/全解除
        group.input.addEventListener("change", () => syncCitiesFromGroup(group));

        // グループ名・件数クリックでもグループのチェックをトグル（アコーディオンボタン以外）
        group.el.querySelector(".p-home-search__group-head")?.addEventListener("click", (event) => {
            if (event.target.closest(".p-home-search__group-toggle")) return;
            if (event.target.closest(".p-home-search__group-check")) return;
            group.input.checked = !group.input.checked;
            syncCitiesFromGroup(group);
        });

        // 市区町村 → 所属グループのチェック状態を同期
        group.cities.forEach((input) => {
            input.addEventListener("change", () => {
                syncGroupsFromCities();
                updateSelection();
            });
        });

        // 地域グループ行＝その市区町村の開閉（SPのみ表示）。
        // 以前は _search.blade.php のインライン <script> で束ねていたが、
        // Ajax注入（innerHTML）では <script> が実行されないためJS側へ移設した
        group.el.querySelector(".p-home-search__group-toggle")?.addEventListener("click", () => {
            const isOpen = group.el.hasAttribute("data-open");
            group.el.toggleAttribute("data-open", !isOpen);
            group.el
                .querySelector(".p-home-search__group-toggle")
                ?.setAttribute("aria-expanded", isOpen ? "false" : "true");
        });
    });

    // 都道府県TOPは初期未選択。グループ／市区の連動はユーザー操作時だけ行う
    syncGroupsFromCities();
    updateSelection();
};

const homeSearchSubmit = () => {
    setupFloating();

    // Ajaxで差し込まれた直後に初期化する。LoadContents は完了時に
    // プレースホルダ上で onLoadContents を発火する（bubbleしないので要素に直接張る）
    document.querySelectorAll(".js-load-contents").forEach((el) => {
        el.addEventListener("onLoadContents", setupSearchForm);
    });

    // フォームが最初からDOMにある場合（Ajaxを介さない描画）にも対応する
    setupSearchForm();
};

export default homeSearchSubmit;
